// src/components/ProfileForm.js
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo
} from "react";
import Sortable from "sortablejs";
import { standardUserApi } from "../services/standardUserApi";
import { useCascadingLocations } from "../hooks/useCascadingLocations";
import "../css/SortableList.css";
import lookupsConfig from "../config/lookups_config.json";
import mixpanel from "mixpanel-browser";

// Export the steps constant so other modules can import it.
export const steps = {
  location: 0,
  topics: 1,
  idealWeekend: 2,
  career: 3,
  movieGenre: 4,
  musicGenre: 5,
  seeking: 6,
  holiday: 7,
  basicInfo1: 8,   // Group 1: gender, birth year
  basicInfo2: 9,   // Group 2: where did you grow up?, do you have children?, relationship status
  basicInfo3: 10,  // Group 3: phone country / code, phone number.
  basicInfo4: 11,  // Group 4: how did you hear about us?
  basicInfo5: 12   // Group 5: first name and last name
};

const ProfileForm = forwardRef(({
  headingText = "Profile Form",
  initialProfileData = null,
  onSubmitSuccess,
  multiStep = false,
  currentStep = 0,
  visibleSteps = null,
  signupType = "casual",
  showSubmitButton = true
}, ref) => {
  // --------------------- STATES ---------------------
  // Basic user info
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [gender, setGender] = useState("");
  const [growUpCountry, setGrowUpCountry] = useState("");
  const [hasChildren, setHasChildren] = useState("");
  const [relationshipStatus, setRelationshipStatus] = useState("");
  const [hearAboutUs, setHearAboutUs] = useState("");

  // Phone fields
  const [phoneCountry, setPhoneCountry] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");

  // Industry & job
  const [industry, setIndustry] = useState("");
  const [job, setJob] = useState("");

  // Location
  const [selectedCountry, setSelectedCountry] = useState("United Kingdom");
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedArea, setSelectedArea] = useState("");

  // Draggable orders – each is an array of objects with { label, order }
  const [allPossibleTopics, setAllPossibleTopics] = useState([]);
  const [preferenceOrder, setPreferenceOrder] = useState([]);
  const [allMovieGenres, setAllMovieGenres] = useState([]);
  const [movieGenreOrder, setMovieGenreOrder] = useState([]);
  const [allMusicGenres, setAllMusicGenres] = useState([]);
  const [musicGenreOrder, setMusicGenreOrder] = useState([]);
  const [allIdealWeekends, setAllIdealWeekends] = useState([]);
  const [idealWeekendOrder, setIdealWeekendOrder] = useState([]);
  const [allSeekingTraits, setAllSeekingTraits] = useState([]);
  const [seekingOrder, setSeekingOrder] = useState([]);
  const [allPossibleHolidays, setAllPossibleHolidays] = useState([]);
  const [holidayOrder, setHolidayOrder] = useState([]);

  // Lookup options for select fields
  const [genders, setGenders] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [growUpCountries, setGrowUpCountries] = useState([]);
  const [hasChildrenOptions, setHasChildrenOptions] = useState([]);
  const [relationshipStatuses, setRelationshipStatuses] = useState([]);
  const [phoneCountries, setPhoneCountries] = useState([]);
  const [hearAboutUsOptions, setHearAboutUsOptions] = useState([]);

  // Error message for multi-step validation
  const [errorMessage, setErrorMessage] = useState("");
  // Flag to determine if the form has been submitted (for signup drop-off tracking)
  const [formSubmitted, setFormSubmitted] = useState(false);

  // --------------------- MIXPANEL TRACKING EFFECTS ---------------------

  // Clear error message whenever the current step changes and track "Signup: Step Viewed"
  useEffect(() => {
    setErrorMessage("");
    if (multiStep && mixpanel && mixpanel.__loaded) {
      mixpanel.track("Signup: Step Viewed", { step: currentStep });
    }
  }, [currentStep, multiStep]);

  // Track form view event on mount (no re-initialization here since it's done in index.js)
  useEffect(() => {
    if (mixpanel && mixpanel.__loaded) {
      if (multiStep) {
        mixpanel.track("Signup: Form Viewed");
      } else {
        mixpanel.track("Edit Profile: Form Viewed");
      }
    }
  }, [multiStep]);

  // Capture NCC parameters from URL on mount (for signup flow)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const nccParam = urlParams.get('ncc');
    const campaignParam = urlParams.get('campaign');

    // Store ncc flag in localStorage if present
    if (nccParam === 'true') {
      localStorage.setItem('ncc', 'true');
      if (campaignParam) {
        localStorage.setItem('ncc_campaign', campaignParam);
      }
    }
  }, []);

  // --------------------- CASCADING LOCATION DATA ---------------------
  const token = localStorage.getItem("memberstackToken");
  const {
    loading: locLoading,
    error: locError,
    getCountries,
    getCitiesForCountry,
    getAreasForCountryCity
  } = useCascadingLocations(token);

  const countries = getCountries();
  const cities = selectedCountry ? getCitiesForCountry(selectedCountry) : [];
  const areas =
    selectedCountry && selectedCity
      ? getAreasForCountryCity(selectedCountry, selectedCity)
      : [];

  // ---------- 1) MAPS: State and Lookups ----------
  const stateMap = {
    firstName: [firstName, setFirstName],
    lastName: [lastName, setLastName],
    birthYear: [birthYear, setBirthYear],
    gender: [gender, setGender],
    growUpCountry: [growUpCountry, setGrowUpCountry],
    hasChildren: [hasChildren, setHasChildren],
    relationshipStatus: [relationshipStatus, setRelationshipStatus],
    hearAboutUs: [hearAboutUs, setHearAboutUs],
    phoneCountry: [phoneCountry, setPhoneCountry],
    phoneNumber: [phoneNumber, setPhoneNumber],
    industry: [industry, setIndustry],
    job: [job, setJob]
  };

  const lookupStateMap = {
    genderOptions: genders,
    growUpCountryOptions: growUpCountries,
    has_children: hasChildrenOptions,
    relationship_status: relationshipStatuses,
    phoneCountries,
    hear_about_us: hearAboutUsOptions,
    industryOptions: industries,
    jobOptions: jobs
  };

  const draggableStateMap = useMemo(() => ({
    preferenceOrder: [preferenceOrder, setPreferenceOrder],
    movieGenreOrder: [movieGenreOrder, setMovieGenreOrder],
    musicGenreOrder: [musicGenreOrder, setMusicGenreOrder],
    idealWeekendOrder: [idealWeekendOrder, setIdealWeekendOrder],
    seekingOrder: [seekingOrder, setSeekingOrder],
    holidayOrder: [holidayOrder, setHolidayOrder]
  }), [
    preferenceOrder,
    movieGenreOrder,
    musicGenreOrder,
    idealWeekendOrder,
    seekingOrder,
    holidayOrder
  ]);

  // ---------- 2) HELPER: Get Year Options ----------
  function getYearOptions() {
    const currentYear = new Date().getFullYear();
    const minAge = 18;
    const maxAge = 110;
    const years = [];
    for (let y = currentYear - minAge; y >= currentYear - maxAge; y--) {
      years.push(y);
    }
    return years;
  }

  // ---------- 3) LOAD LOOKUPS ----------
  useEffect(() => {
    async function fetchLookupData() {
      try {
        const lookupData = await standardUserApi.getLookupData();

        // Simple arrays
        const simpleLookupMapping = [
          { stateSetter: setGenders, apiKey: "gender" },
          { stateSetter: setIndustries, apiKey: "industry" },
          { stateSetter: setJobs, apiKey: "job" },
          { stateSetter: setGrowUpCountries, apiKey: "grow_up_country" },
          { stateSetter: setHasChildrenOptions, apiKey: "has_children" },
          { stateSetter: setRelationshipStatuses, apiKey: "relationship_status" },
          { stateSetter: setPhoneCountries, apiKey: "phoneCountries" },
          { stateSetter: setHearAboutUsOptions, apiKey: "hear_about_us" }
        ];
        simpleLookupMapping.forEach(({ stateSetter, apiKey }) => {
          stateSetter(lookupData[apiKey] || []);
        });

        // Order-based arrays for draggable
        const orderMapping = [
          { stateSetter: setAllPossibleTopics, apiKey: "conversation_topic" },
          { stateSetter: setAllMovieGenres, apiKey: "movie_genre" },
          { stateSetter: setAllMusicGenres, apiKey: "music_genre" },
          { stateSetter: setAllIdealWeekends, apiKey: "ideal_weekend" },
          { stateSetter: setAllSeekingTraits, apiKey: "seeking_trait" },
          { stateSetter: setAllPossibleHolidays, apiKey: "holiday" }
        ];
        orderMapping.forEach(({ stateSetter, apiKey }) => {
          const arr = lookupData[apiKey] || [];
          const orderArr = arr.map((item, idx) => ({
            label: item,
            order: idx + 1
          }));
          stateSetter(orderArr);
        });
      } catch (err) {
        // ignore
      }
    }
    fetchLookupData();
  }, []);

  // ---------- 4) LOAD INITIAL PROFILE DATA ----------
  useEffect(() => {
    if (!initialProfileData) return;

    // Basic fields (matching your API’s keys)
    const basicFieldSetters = {
      firstName: setFirstName,
      lastName: setLastName,
      gender: setGender,
      grow_up_country: setGrowUpCountry,
      has_children: setHasChildren,
      relationship_status: setRelationshipStatus,
      hear_about_us: setHearAboutUs,
      industry: setIndustry,
      job: setJob,
      phoneNumber: setPhoneNumber
    };
    Object.entries(basicFieldSetters).forEach(([field, setter]) => {
      const value = initialProfileData[field];
      if (value !== undefined && value !== null) {
        setter(value);
      }
    });

    // birthYear (support both camelCase & snake_case)
    const nowYear = new Date().getFullYear();
    const minB = nowYear - 110;
    const maxB = nowYear - 18;
    const by = initialProfileData.birthYear ?? initialProfileData.birth_year;
    if (by >= minB && by <= maxB) {
      setBirthYear(by.toString());
    } else {
      setBirthYear("");
    }

    // *** NEW: phone country code ***
    const pc = initialProfileData.phone_country_id ?? initialProfileData.phone_country;
    if (pc !== undefined && pc !== null) {
      setPhoneCountry(pc.toString());
    } else {
      setPhoneCountry("");
    }

    // location
    if (initialProfileData.location) {
      setSelectedCountry(initialProfileData.location.country || "");
      setSelectedCity(initialProfileData.location.city || "");
      setSelectedArea(initialProfileData.location.area || "");
    }

    // Draggable orders
    function parseOrderData(orderData) {
      return Object.entries(orderData)
        .map(([key, order]) => ({
          label: key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
          order
        }))
        .sort((a, b) => a.order - b.order);
    }
    const orderFieldSetters = {
      // conversation_topic: setPreferenceOrder,
      // movie_genre: setMovieGenreOrder,
      // music_genre: setMusicGenreOrder,
      // ideal_weekend: setIdealWeekendOrder,
      // seeking_trait: setSeekingOrder,
      // holiday: setHolidayOrder
      topicsOrder: setPreferenceOrder,
      movieGenreOrder: setMovieGenreOrder,
      musicGenreOrder: setMusicGenreOrder,
      idealWeekendOrder: setIdealWeekendOrder,
      seekingOrder: setSeekingOrder,
      holidaysOrder: setHolidayOrder
    };
    Object.entries(orderFieldSetters).forEach(([field, setter]) => {
      if (initialProfileData[field]) {
        setter(parseOrderData(initialProfileData[field]));
      }
    });
  }, [initialProfileData]);


  // ---------- 5) LOAD FROM LOCALSTORAGE IF MULTISTEP & NO initialProfileData ----------
  useEffect(() => {
    if (!multiStep) return;
    if (initialProfileData) return;

    const storedString = localStorage.getItem("profileData");
    if (!storedString) return;

    try {
      const storedPayload = JSON.parse(storedString);

      const basicFieldSetters = {
        first_name: setFirstName,
        last_name: setLastName,
        birthYear: setBirthYear,
        gender: setGender,
        industry: setIndustry,
        job: setJob,
        grow_up: setGrowUpCountry,
        has_children: setHasChildren,
        relationship_status: setRelationshipStatus,
        hear_about_us: setHearAboutUs
      };
      Object.entries(basicFieldSetters).forEach(([field, setter]) => {
        if (storedPayload[field] !== undefined) {
          setter(storedPayload[field]);
        }
      });

      if (storedPayload.location) {
        setSelectedCountry(storedPayload.location.country || "");
        setSelectedCity(storedPayload.location.city || "");
        setSelectedArea(storedPayload.location.area || "");
      }

      function parseOrderData(orderData) {
        return Object.entries(orderData)
          .map(([key, order]) => ({
            label: key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()),
            order
          }))
          .sort((a, b) => a.order - b.order);
      }

      const orderFieldsMap = {
        topicsOrder: setPreferenceOrder,
        movieGenreOrder: setMovieGenreOrder,
        musicGenreOrder: setMusicGenreOrder,
        idealWeekendOrder: setIdealWeekendOrder,
        seekingOrder: setSeekingOrder,
        holidaysOrder: setHolidayOrder
      };
      Object.entries(orderFieldsMap).forEach(([field, setter]) => {
        if (storedPayload[field]) {
          setter(parseOrderData(storedPayload[field]));
        }
      });
    } catch (err) {
      // ignore
    }
  }, [multiStep, initialProfileData]);

  // ---------- 6) FALLBACKS if user’s order arrays are empty ----------
  useEffect(() => {
    const fallbackMapping = [
      { order: preferenceOrder, all: allPossibleTopics, setter: setPreferenceOrder },
      { order: movieGenreOrder, all: allMovieGenres, setter: setMovieGenreOrder },
      { order: musicGenreOrder, all: allMusicGenres, setter: setMusicGenreOrder },
      { order: idealWeekendOrder, all: allIdealWeekends, setter: setIdealWeekendOrder },
      { order: seekingOrder, all: allSeekingTraits, setter: setSeekingOrder },
      { order: holidayOrder, all: allPossibleHolidays, setter: setHolidayOrder }
    ];
    fallbackMapping.forEach(({ order, all, setter }) => {
      if (order.length === 0 && all.length > 0) {
        setter(all);
      }
    });
  }, [
    preferenceOrder, allPossibleTopics,
    movieGenreOrder, allMovieGenres,
    musicGenreOrder, allMusicGenres,
    idealWeekendOrder, allIdealWeekends,
    seekingOrder, allPossibleTraits => allPossibleTraits, // slight typo fix if needed
    holidayOrder, allPossibleHolidays
  ]);

  // ---------- 7) DEFAULT PHONE COUNTRY FOR MULTI-STEP SIGNUP ----------
  useEffect(() => {
    if (multiStep && phoneCountries.length > 0 && !phoneCountry) {
      const ukOption = phoneCountries.find(opt =>
        opt.display.toLowerCase().includes("united kingdom")
      );
      if (ukOption) {
        setPhoneCountry(ukOption.id);
      }
    }
  }, [multiStep, phoneCountries, phoneCountry]);

  // ---------- 8) VALIDATION FOR MULTI-STEP ----------
  function validateRequiredFieldsForStep() {
    if (!multiStep) return true;
    let missingRequired = false;

    // When visibleSteps is provided, map currentStep index to actual step number
    const actualStep = visibleSteps ? visibleSteps[currentStep] : currentStep;

    if (actualStep === steps.location) {
      missingRequired = !selectedCountry || !selectedCity || !selectedArea;
    } else if (actualStep === steps.career) {
      missingRequired = !industry || !job;
    } else if (actualStep === steps.basicInfo1) {
      missingRequired = !gender || !birthYear;
    } else if (actualStep === steps.basicInfo2) {
      missingRequired = !growUpCountry || !hasChildren || !relationshipStatus;
    } else if (actualStep === steps.basicInfo3) {
      // Phone fields are now optional - only validate format if provided
      if (phoneNumber.trim()) {
        const stripped = phoneNumber.replace(/^0+/, "");
        if (stripped.length < 7 || stripped.length > 15) {
          missingRequired = true;
        }
      }
    } else if (actualStep === steps.basicInfo4) {
      missingRequired = !hearAboutUs;
    } else if (actualStep === steps.basicInfo5) {
      missingRequired = !firstName.trim() || !lastName.trim();
    }

    if (missingRequired) {
      setErrorMessage("Please fill in all required fields for this step.");
      return false;
    }
    setErrorMessage("");
    return true;
  }

  // ---------- New Functions: Basic Info Groups ----------
  function renderBasicInfoGroup1() {
    const years = getYearOptions();
    return (
      <>
        {renderSelectField("gender")}
        <label>Birth Year</label>
        <select
          className="hero_form-textfield w-input"
          value={birthYear}
          onChange={e => setBirthYear(e.target.value)}
        >
          <option value="">Select your birth year...</option>
          {years.map(y => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </>
    );
  }

  function renderBasicInfoGroup2() {
    return (
      <>
        {renderSelectField("grow_up")}
        {renderSelectField("has_children")}
        {renderSelectField("relationship_status")}
      </>
    );
  }

  function renderBasicInfoGroup3() {
    return (
      <>
        <label>Phone Country / Code <em style={{ color: "#666" }}>(Optional)</em></label>
        <select
          className="hero_form-textfield w-input"
          value={phoneCountry}
          onChange={e => setPhoneCountry(e.target.value)}
        >
          <option value="">Select phone code</option>
          {phoneCountries.map(option => (
            <option key={option.id} value={option.id}>
              {option.display}
            </option>
          ))}
        </select>
        <label>Phone Number <em style={{ color: "#666" }}>(Optional - only for important alerts)</em></label>
        <input
          type="text"
          className="hero_form-textfield w-input"
          value={phoneNumber}
          onChange={e => setPhoneNumber(e.target.value)}
          placeholder="e.g., 07123456789"
        />
      </>
    );
  }

  function renderBasicInfoGroup4() {
    return (
      <>
        {renderSelectField("hear_about_us")}
      </>
    );
  }

  function renderBasicInfoGroup5() {
    return (
      <>
        {renderTextField("first_name")}
        {renderTextField("last_name")}
      </>
    );
  }

  function renderBasicInfoCombined() {
    return (
      <>
        {renderBasicInfoGroup1()}
        <hr style={{ borderTop: "1px dashed #ccc" }} />
        {renderBasicInfoGroup2()}
        <hr style={{ borderTop: "1px dashed #ccc" }} />
        {renderBasicInfoGroup3()}
        <hr style={{ borderTop: "1px dashed #ccc" }} />
        {renderBasicInfoGroup4()}
        <hr style={{ borderTop: "1px dashed #ccc" }} />
        {renderBasicInfoGroup5()}
      </>
    );
  }

  // ---------- 10) RENDERING BASIC FIELDS ----------
  function renderTextField(fieldName) {
    const fieldDef = lookupsConfig.textFields.find(f => f.fieldName === fieldName);
    if (!fieldDef) return null;
    const { label, stateKey, idx } = fieldDef;
    const [value, setValue] = stateMap[stateKey] || [];
    return (
      <React.Fragment key={idx}>
        <label>{label}</label>
        <input
          type="text"
          className="hero_form-textfield w-input"
          value={value}
          onChange={e => setValue(e.target.value)}
        />
      </React.Fragment>
    );
  }

  function renderSelectField(fieldName) {
    const fieldDef = lookupsConfig.selectFields.find(f => f.fieldName === fieldName);
    if (!fieldDef) return null;
    const { label, stateKey, idx, type, optionsStateKey } = fieldDef;
    const [value, setValue] = stateMap[stateKey] || [];
    let options = [];

    if (type === "year-select") {
      options = getYearOptions();
    } else if (optionsStateKey && lookupStateMap[optionsStateKey]) {
      options = lookupStateMap[optionsStateKey];
    }
    return (
      <React.Fragment key={idx}>
        <label>{label}</label>
        <select
          className="hero_form-textfield w-input"
          value={value}
          onChange={e => setValue(e.target.value)}
        >
          <option value="">Select {label}</option>
          {options.map((opt, i) => (
            <option key={i} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </React.Fragment>
    );
  }

  // ---------- 11) MULTI-STEP RENDER ----------
  function renderStep() {
    // When visibleSteps is provided, map currentStep index to actual step number
    const actualStep = visibleSteps ? visibleSteps[currentStep] : currentStep;

    // Handle intro step (-1) - return null as it's rendered in GatheringMatcher
    if (actualStep === -1) return null;

    const draggable = lookupsConfig.draggableSections.find(
      sec => sec.stepIndex === actualStep
    );
    if (draggable) return renderDraggableSection(draggable.stateKey);

    if (actualStep === steps.location) return renderLocationFields();
    if (actualStep === steps.career) return renderCareerFields();
    if (actualStep === steps.basicInfo1) return renderBasicInfoGroup1();
    if (actualStep === steps.basicInfo2) return renderBasicInfoGroup2();
    if (actualStep === steps.basicInfo3) return renderBasicInfoGroup3();
    if (actualStep === steps.basicInfo4) return renderBasicInfoGroup4();
    if (actualStep === steps.basicInfo5) return renderBasicInfoGroup5();
    return null;
  }

  function renderAllSections() {
    return (
      <>
        {renderBasicInfoGroup5()}
        {renderLocationFields()}
        {renderBasicInfoGroup3()}
      </>
    );
  }

  // ---------- 9) RENDER DRAGGABLE SECTION ----------
  function renderDraggableSection(sectionKey) {
    const sectionDef = lookupsConfig.draggableSections.find(
      sec => sec.stateKey === sectionKey
    );
    if (!sectionDef) return null;
    const { title, elementId, stateKey } = sectionDef;
    const [items] = draggableStateMap[stateKey] || [[], null];
    return (
      <div key={stateKey}>
        <h4 style={{ textAlign: "center" }}>{title}</h4>
        <div id={elementId} style={{ marginBottom: "20px" }}>
          {items.map(item => (
            <div key={item.label} className="draggable-card" data-label={item.label}>
              <div className="drag-handle">☰</div>
              {item.label}
            </div>
          ))}
        </div>
      </div>
    );
  }

  function renderCareerFields() {
    return (
      <>
        {renderSelectField("job")}
        {renderSelectField("industry")}
      </>
    );
  }

  function renderLocationFields() {
    return (
      <>
        <h4 style={{ textAlign: "center" }}>📍 Where would you like to meet people?</h4>
        <label>🌎 Country</label>
        <select
          className="hero_form-textfield w-input"
          value={selectedCountry}
          onChange={e => {
            setSelectedCountry(e.target.value);
            setSelectedCity("");
            setSelectedArea("");
          }}
        >
          <option value="">Select country</option>
          {countries.map(c => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <label>🏙 City</label>
        <select
          className="hero_form-textfield w-input"
          value={selectedCity}
          onChange={e => {
            const newCity = e.target.value;
            setSelectedCity(newCity);
            const newAreas = newCity ? getAreasForCountryCity(selectedCountry, newCity) : [];
            setSelectedArea(newAreas.length === 1 ? newAreas[0] : "");
          }}
          disabled={!selectedCountry}
        >
          <option value="">Select city</option>
          {cities.map(ct => (
              <option key={ct} value={ct}>
                {ct}
              </option>
            ))}
        </select>
        {areas.length > 1 && (
          <>
            <label>🧭 Area</label>
            <select
              className="hero_form-textfield w-input"
              value={selectedArea}
              onChange={e => setSelectedArea(e.target.value)}
              disabled={!selectedCity}
            >
              <option value="">Select area</option>
              {areas.map(ar => (
                <option key={ar} value={ar}>
                  {ar}
                </option>
              ))}
            </select>
          </>
        )}
      </>
    );
  }

  // ---------- 12) PAYLOAD UTILITY ----------
  function getFormPayload() {
    function arrayToObj(arr) {
      return arr.reduce((acc, item) => {
        const key = item.label
          .toLowerCase()
          .replace(/[^\w]+/g, "_")
          .replace(/^_+|_+$/g, "");
        acc[key] = item.order;
        return acc;
      }, {});
    }
    return {
      firstName,
      lastName,
      birthYear: birthYear ? parseInt(birthYear, 10) : null,
      gender,
      growUpCountry,
      hasChildren,
      relationshipStatus,
      industry,
      job,
      phoneCountryId: phoneCountry || null,
      phoneNumber: phoneNumber.trim() || null,
      hearAboutUs,
      location: {
        country: selectedCountry,
        city: selectedCity,
        area: selectedArea
      },
      topicsOrder: arrayToObj(preferenceOrder),
      movieGenreOrder: arrayToObj(movieGenreOrder),
      musicGenreOrder: arrayToObj(musicGenreOrder),
      idealWeekendOrder: arrayToObj(idealWeekendOrder),
      seekingOrder: arrayToObj(seekingOrder),
      holidaysOrder: arrayToObj(holidayOrder),
      // Include signup type (networking, complete_networking, or casual)
      signupType: signupType
    };
  }

  // ---------- 13) SUBMIT HANDLER ----------
  async function handleSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();

    // --------------- SINGLE-PAGE VALIDATION (only when NOT in multi-step mode) ---------------
    if (!multiStep) {
      if (!firstName.trim() || !lastName.trim()) {
        alert("Please enter your first and last name before saving.");
        return;
      }
      // Optional phone validation - only check format if provided
      if (phoneNumber.trim()) {
        const stripped = phoneNumber.replace(/^0+/, "");
        if (stripped.length < 7 || stripped.length > 15) {
          alert("Phone number must be between 7 and 15 digits (excluding leading zeros).");
          return;
        }
      }
    }

    try {
      const payload = getFormPayload();
      if (multiStep) {
        // For multi-step, we've already validated phone length on the correct step
        localStorage.setItem("profileData", JSON.stringify(payload));
        // Store signup type separately so it persists throughout the journey
        localStorage.setItem("signupType", signupType);
        if (mixpanel && mixpanel.__loaded) {
          mixpanel.track("Signup: Completed", { step: currentStep });
        }
        setFormSubmitted(true);
        if (onSubmitSuccess) onSubmitSuccess();
      } else {
        const localToken = localStorage.getItem("memberstackToken");
        if (!localToken) throw new Error("No token found");
        await standardUserApi.updateUserProfile(localToken, payload);
        if (mixpanel && mixpanel.__loaded) {
          mixpanel.track("Profile Updated");
        }
        if (onSubmitSuccess) onSubmitSuccess();
      }
    } catch (err) {
      alert("Failed to update your profile. Please try again.");
    }
  }

  // ---------- 14) IMPERATIVE HANDLE FOR PARENT COMPONENTS ----------
  useImperativeHandle(ref, () => ({
    submitForm: handleSubmit,
    getFormPayload: getFormPayload,
    validateRequiredFieldsForStep: validateRequiredFieldsForStep
  }));

  // ---------- 15) DRAGGABLE INITIALIZATION ----------
  useEffect(() => {
    const sortableInstances = [];
    if (multiStep) {
      const draggableSection = lookupsConfig.draggableSections.find(
        sec => sec.stepIndex === currentStep
      );
      if (draggableSection) {
        const container = document.getElementById(draggableSection.elementId);
        if (container) {
          const sortable = Sortable.create(container, {
            animation: 150,
            onEnd: evt => {
              const stateKey = draggableSection.stateKey;
              const setter = draggableStateMap[stateKey][1];
              const newOrder = Array.from(evt.to.children).map((child, index) => ({
                label: child.getAttribute("data-label"),
                order: index + 1
              }));
              setter(newOrder);
            }
          });
          sortableInstances.push(sortable);
        }
      }
    } else {
      lookupsConfig.draggableSections.forEach(section => {
        const container = document.getElementById(section.elementId);
        if (container) {
          const sortable = Sortable.create(container, {
            animation: 150,
            onEnd: evt => {
              const stateKey = section.stateKey;
              const setter = draggableStateMap[stateKey][1];
              const newOrder = Array.from(evt.to.children).map((child, index) => ({
                label: child.getAttribute("data-label"),
                order: index + 1
              }));
              setter(newOrder);
            }
          });
          sortableInstances.push(sortable);
        }
      });
    }
    // Cleanup: destroy all sortable instances on re-run.
    return () => {
      sortableInstances.forEach(instance => instance.destroy());
    };
  }, [multiStep, currentStep, draggableStateMap]);

  // ---------- 16) RENDER ----------
  return (
    <div>
      <h4 style={{
        textAlign: "center",
        fontSize: "1.5rem !important"  // Force override
      }}>
        {headingText}
      </h4>
      {(locLoading || locError) && <p style={{ color: "red" }}>{locError}</p>}
      {errorMessage && (
        <p style={{ color: "red", textAlign: "center", marginTop: "1rem" }}>
          {errorMessage}
        </p>
      )}
      <form onSubmit={handleSubmit}>
        {multiStep ? renderStep() : renderAllSections()}
        {showSubmitButton && (
          <button
            type="submit"
            className="button-2 is-black w-button"
            style={{ marginTop: 16 }}
          >
            Save
          </button>
        )}
      </form>
    </div>
  );
});

export default ProfileForm;
